import React, { Component, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  shouldRedirect: boolean;
  errorCount: number;
}

class ChatErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;
  
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      shouldRedirect: false,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is React Error #185 (Suspense during sync update)
    const isError185 = error.message?.includes('185') || 
                       error.message?.includes('suspended') ||
                       error.message?.includes('Minified React error #185');
    const isTransientLengthRead = /Cannot read properties of undefined \(reading 'length'\)/i.test(error.message || '');

    console.error('[ChatErrorBoundary] Caught error:', {
      message: error.message,
      isError185,
      timestamp: new Date().toISOString()
    });
    // For Error #185 (component suspended during synchronous input),
    // do NOT trip the boundary UI — log and allow React to recover.
    if (isError185 || isTransientLengthRead) {
      return {
        hasError: false,
        error: null,
        shouldRedirect: false,
      };
    }

    return {
      hasError: true,
      error,
      // Don't redirect immediately; allow user to retry
      shouldRedirect: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ChatErrorBoundary] Error details:', {
      error: error.message,
      componentStack: errorInfo.componentStack?.split('\n').slice(0, 5).join('\n'),
      timestamp: new Date().toISOString()
    });
    
    const isError185 = error.message?.includes('185') || error.message?.includes('suspended');
    const isTransientLengthRead = /Cannot read properties of undefined \(reading 'length'\)/i.test(error.message || '');

    // Only count non-185 errors toward redirect threshold
    if (!isError185 && !isTransientLengthRead) {
      this.setState(prevState => ({ errorCount: prevState.errorCount + 1 }));
      if (this.state.errorCount >= 3) {
        console.warn('[ChatErrorBoundary] Too many errors, redirecting to /chat');
        this.setState({ shouldRedirect: true });
      }
    }
    
    // Auto-retry after a delay for Error #185 with exponential backoff
    if (error.message?.includes('185')) {
      if (this.resetTimeoutId) {
        clearTimeout(this.resetTimeoutId);
      }
      
      // Exponential backoff: 500ms, 1000ms, 2000ms
      const retryDelay = Math.min(500 * Math.pow(2, Math.max(0, this.state.errorCount - 1)), 2000);
      
      this.resetTimeoutId = setTimeout(() => {
        console.log(`[ChatErrorBoundary] Auto-retrying after Error #185 (attempt ${this.state.errorCount}, delay ${retryDelay}ms)`);
        this.setState({ hasError: false, error: null });
      }, retryDelay);
    }
  }
  
  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }
  
  handleReset = () => {
    this.setState({ hasError: false, error: null, errorCount: 0 });
  };
  
  handleNavigateHome = () => {
    this.setState({ shouldRedirect: true });
  };

  render() {
    if (this.state.shouldRedirect) {
      return <Navigate to="/chat" replace />;
    }
    
    if (this.state.hasError) {
      const isError185 = this.state.error?.message?.includes('185');
      
      // For Error #185, show a minimal loading state while auto-retry happens
      if (isError185 && this.state.errorCount < 3) {
        return (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            color: 'var(--color-text-muted)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div>Loading chat...</div>
              {this.state.errorCount > 1 && (
                <div style={{ marginTop: '10px', fontSize: '0.9em' }}>
                  Retry attempt {this.state.errorCount}...
                </div>
              )}
            </div>
          </div>
        );
      }
      
      // For other errors or after multiple retries, show error UI
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          maxWidth: '600px',
          margin: '100px auto'
        }}>
          <h2 style={{ marginBottom: '20px', color: 'var(--color-text)' }}>
            Chat Loading Error
          </h2>
          <p style={{ marginBottom: '30px', color: 'var(--color-text-muted)' }}>
            We encountered an issue loading the chat. This can happen during navigation or when switching conversations quickly.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={this.handleNavigateHome}
              style={{
                padding: '10px 20px',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Start New Chat
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '30px', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--color-text-muted)' }}>
                Error Details (Development Only)
              </summary>
              <pre style={{ 
                marginTop: '10px', 
                padding: '10px',
                background: 'var(--color-bg-secondary)',
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto'
              }}>
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChatErrorBoundary;
